import type { WaterfallStep } from '@financial/shared';
import { WaterfallChart } from '../charts/DashboardCharts';
import { formatMoney } from '../../lib/format';

/**
 * En pantallas pequeñas una cascada con ejes y etiquetas inclinadas obliga a
 * depender del tooltip. Esta versión conserva la gráfica en escritorio y
 * muestra la misma operación como una lista verificable en móvil.
 */
export function ResponsiveWaterfall({ steps }: { steps: WaterfallStep[] }) {
  return (
    <>
      <div className="sm:hidden">
        <WaterfallEquation steps={steps} />
      </div>
      <div className="hidden sm:block">
        <WaterfallChart steps={steps} />
      </div>
    </>
  );
}

function WaterfallEquation({ steps }: { steps: WaterfallStep[] }) {
  if (steps.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-slate-600">
        Aún no hay datos para calcular el cierre.
      </p>
    );
  }

  return (
    <div
      className="mt-3 rounded-xl border border-slate-100 bg-slate-50/70 p-3"
      aria-label="Cálculo del cierre estimado"
    >
      <p className="text-xs font-semibold text-slate-700">Cálculo del cierre estimado</p>
      <ol className="mt-2 divide-y divide-slate-200/80">
        {steps.map((step) => {
          const isResult = step.type === 'total';
          const sign = step.type === 'decrease' ? '−' : step.type === 'increase' ? '+' : '';
          const amount = isResult ? formatMoney(step.value) : formatMoney(Math.abs(step.value));
          const amountTone = isResult
            ? step.value >= 0
              ? 'text-income'
              : 'text-expense'
            : step.type === 'decrease'
              ? 'text-expense'
              : 'text-slate-900';

          return (
            <li
              key={`${step.type}-${step.label}`}
              className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-2 text-sm ${isResult ? 'font-semibold' : ''}`}
            >
              <span className="min-w-0 text-slate-700">
                {isResult ? '= ' : sign ? `${sign} ` : ''}
                {step.label}
              </span>
              <span className={`whitespace-nowrap tabular-nums ${amountTone}`}>{amount}</span>
            </li>
          );
        })}
      </ol>
      <p className="mt-2 text-[11px] leading-4 text-slate-500">
        Es un cálculo estimado: cambia al registrar dinero que entra, gastos, pagos o ahorro.
      </p>
    </div>
  );
}
